import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { ChevronDown, FileText, LogIn, LogOut, Menu as MenuIcon, User } from 'lucide-react'
import { useState } from 'react'
import { useAuth } from 'react-oidc-context'

import { LOGIN_ROUTE, safeRedirectPath } from '@/lib/auth'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Divider } from '@/components/ui/divider'
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '@/components/ui/dropdown'
import { Container } from '@/components/ui/grid'
import {
  Header,
  HeaderActions,
  HeaderBottom,
  HeaderInfo,
  HeaderLinks,
  HeaderLogo,
  HeaderMenu,
  HeaderSign,
  HeaderSubtitle,
  HeaderTitle,
  HeaderTop,
} from '@/components/ui/header'
import { Menu, MenuBody, MenuContent, MenuItem, MenuTrigger } from '@/components/ui/menu'
import { SignIn } from '@/components/ui/sign-in'

/** Faixa superior do Header do DS — hiperlinks de acesso rápido. */
const LINKS_INSTITUCIONAIS = [
  { rotulo: 'Órgãos do Governo', href: 'https://www.gov.br/pt-br/orgaos-do-governo' },
  { rotulo: 'Acesso à Informação', href: 'https://www.gov.br/acessoainformacao/pt-br' },
  { rotulo: 'Legislação', href: 'https://www.gov.br/pt-br/legislacao' },
  {
    rotulo: 'Acessibilidade',
    href: 'https://www.gov.br/governodigital/pt-br/acessibilidade-digital',
  },
] as const

const ASSINATURA = 'Ministério do Meio Ambiente'
const TITULO_APLICACAO = 'Outros Mecanismos Eficazes de Conservação (OMECs)'
const SUBTITULO_APLICACAO = 'Secretaria Nacional de Biodiversidade, Florestas e Direitos Animais'

/**
 * Clique que o roteador deve interceptar: botão principal e sem modificador.
 * Com Ctrl/Cmd/Shift/Alt o browser abre em nova aba ou janela, e engolir isso
 * com `preventDefault` tiraria do usuário um comportamento que ele espera de
 * qualquer link.
 */
function cliqueSimples(evento: React.MouseEvent): boolean {
  return (
    !evento.defaultPrevented &&
    evento.button === 0 &&
    !evento.metaKey &&
    !evento.ctrlKey &&
    !evento.shiftKey &&
    !evento.altKey
  )
}

/**
 * Iniciais para o avatar do usuário autenticado. Duas letras no máximo: é o que
 * cabe no círculo do DS sem reduzir a tipografia.
 */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean)
  const primeira = partes.at(0)?.at(0) ?? ''
  const ultima = partes.length > 1 ? (partes.at(-1)?.at(0) ?? '') : ''
  return `${primeira}${ultima}`
}

/*
 * Header do DS, montado pelas partes do registry gov.br (`@shadcn-govbr/header`)
 * na ordem que a anatomia do componente estabelece:
 *
 * - `HeaderTop`: logo e assinatura à esquerda; à direita a área para links, o
 *   separador e o bloco de acesso — que é o **botão autenticar** enquanto não há
 *   sessão e o **avatar do usuário com lista suspensa** depois que há. Os dois
 *   ocupam a mesma posição, a extrema direita da faixa.
 * - `HeaderBottom`: botão de menu, depois título e subtítulo.
 *
 * Da anatomia, três itens ficam de fora por não terem conteúdo neste produto: a
 * área para funcionalidades, o campo de busca e a lista suspensa que recolhe os
 * links de acesso abaixo de 1280px (o registry simplesmente os esconde ali).
 *
 * Navegação da aplicação fica no menu (drawer) aberto pelo botão de menu; o que
 * é da conta de quem entrou — perfil e sair — fica na lista suspensa do avatar.
 * Nenhum dos dois vira linha solta no cabeçalho, que a anatomia não prevê.
 */
export function AppHeader() {
  const auth = useAuth()
  const navigate = useNavigate()
  const [menuAberto, setMenuAberto] = useState(false)
  // Entrar pelo header devolve o usuário à página em que ele estava.
  const currentPath = useRouterState({
    select: (state) => safeRedirectPath(state.location.href),
  })
  // Na própria tela de login o botão do header é ruído — e, pior, sobrescreveria
  // o `redirect` que trouxe o usuário até ali com a raiz.
  const onLoginScreen = useRouterState({
    select: (state) => state.location.pathname === LOGIN_ROUTE,
  })
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  const nomeUsuario = auth.user?.profile.name ?? auth.user?.profile.preferred_username ?? ''

  return (
    // O fundo e a sombra do cabeçalho vão de borda a borda; o conteúdo, não —
    // ele fica no `Container`, na mesma caixa centralizada do resto das telas,
    // como no portal gov.br. O recuo lateral que o registry põe na faixa é
    // zerado porque o `Container` já traz o dele, e os dois somariam.
    <Header sticky className="px-0 min-[576px]:px-0">
      <Container>
        <HeaderTop>
          <HeaderLogo>
            <a href="https://www.gov.br/pt-br" aria-label="Portal Gov.br">
              {/* A altura vai explícita porque o SVG traz só `viewBox`, sem
                dimensão intrínseca: dentro do flex do `HeaderLogo` a largura
                resolveria para zero e a marca sumiria. `--header-logo-size` é o
                token que o próprio registry escalona por breakpoint. */}
              <img src="/govbr.svg" alt="gov.br" className="h-(--header-logo-size) w-auto" />
            </a>
            <HeaderSign>{ASSINATURA}</HeaderSign>
          </HeaderLogo>

          <HeaderActions>
            <HeaderLinks aria-label="Links institucionais">
              {LINKS_INSTITUCIONAIS.map((link) => (
                <a key={link.rotulo} href={link.href}>
                  {link.rotulo}
                </a>
              ))}
            </HeaderLinks>

            {/* Separador entre os grupos da direita. A anatomia do Header marca os
              separadores como item **obrigatório**, e é ele que divide os
              hiperlinks do bloco de acesso. Só existe onde os hiperlinks
              existem — abaixo de 1280px o registry os esconde. */}
            {onLoginScreen && !auth.isAuthenticated ? null : (
              <Divider
                orientation="vertical"
                className="hidden h-(--spacing-scale-5x) min-[1280px]:block"
              />
            )}

            {auth.isAuthenticated ? (
              // Autenticado, o lugar do "Botão Autenticar" passa a ser o "Avatar do
              // Usuário" com sua lista suspensa — os itens 8, 9 e 11 da anatomia.
              <Dropdown>
                <DropdownTrigger asChild>
                  <button
                    type="button"
                    className="flex cursor-pointer items-center gap-(--spacing-scale-half) rounded-full border-0 bg-transparent p-0 text-(--interactive) outline-none focus-visible:[outline:var(--focus-width)_dashed_var(--focus-color)] focus-visible:[outline-offset:var(--focus-offset)]"
                  >
                    <Avatar type="letter">
                      <AvatarFallback>{iniciais(nomeUsuario)}</AvatarFallback>
                    </Avatar>
                    <span className="sr-only">{nomeUsuario}</span>
                    <ChevronDown aria-hidden="true" className="size-(--icon-size-base)" />
                  </button>
                </DropdownTrigger>

                <DropdownContent aria-label={nomeUsuario}>
                  <DropdownItem
                    onSelect={() => {
                      void navigate({ to: '/perfil' })
                    }}
                  >
                    <User aria-hidden="true" />
                    Meu perfil
                  </DropdownItem>
                  <DropdownItem onSelect={() => void auth.signoutRedirect()}>
                    <LogOut aria-hidden="true" />
                    Sair
                  </DropdownItem>
                </DropdownContent>
              </Dropdown>
            ) : onLoginScreen ? null : (
              // O header manda para a tela de login em vez de disparar o redirect
              // direto: a tela de login precisa existir, e ela é o único lugar que
              // sabe montar o `state` com a rota de retorno.
              <SignIn variant="primary" asChild>
                <Link to={LOGIN_ROUTE} search={{ redirect: currentPath }}>
                  <LogIn aria-hidden="true" />
                  Entrar com GOV.BR
                </Link>
              </SignIn>
            )}
          </HeaderActions>
        </HeaderTop>

        <HeaderBottom>
          <HeaderMenu>
            {auth.isAuthenticated ? (
              <Menu open={menuAberto} onOpenChange={setMenuAberto}>
                <MenuTrigger asChild>
                  <Button variant="tertiary" circle aria-label="Abrir menu">
                    <MenuIcon aria-hidden="true" />
                  </Button>
                </MenuTrigger>

                {/*
                 * O `asChild` do `MenuItem` do registry não funciona: ele entrega
                 * três filhos ao `Slot` do Radix (ícone, conteúdo, apoio) sem
                 * `Slottable`, e o Slot exige um só — abrir o menu derrubava a
                 * tela. Por isso a navegação não usa o `Link` do router: o item
                 * fica sendo o `<a href>` que o próprio componente renderiza, e o
                 * clique comum é interceptado para navegar sem recarregar. Clique
                 * com modificador segue o caminho nativo do browser (nova aba com
                 * a URL certa), como o `Link` faria.
                 */}
                <MenuContent title={TITULO_APLICACAO}>
                  <MenuBody aria-label="Navegação principal">
                    <MenuItem
                      href="/cadastros"
                      icon={<FileText aria-hidden="true" />}
                      active={pathname.startsWith('/cadastros')}
                      onClick={(evento) => {
                        if (cliqueSimples(evento)) {
                          evento.preventDefault()
                          setMenuAberto(false)
                          void navigate({ to: '/cadastros' })
                        }
                      }}
                    >
                      Meus cadastros
                    </MenuItem>
                  </MenuBody>
                </MenuContent>
              </Menu>
            ) : null}

            <HeaderInfo>
              <HeaderTitle>
                <Link to="/">
                  {/* 576px é o degrau do DS, não o `sm` do Tailwind (640px) —
                    abaixo dele o registry já esconde assinatura e subtítulo. */}
                  <span className="min-[576px]:hidden">OMECs</span>
                  <span className="hidden min-[576px]:inline">{TITULO_APLICACAO}</span>
                </Link>
              </HeaderTitle>
              <HeaderSubtitle>{SUBTITULO_APLICACAO}</HeaderSubtitle>
            </HeaderInfo>
          </HeaderMenu>
        </HeaderBottom>
      </Container>
    </Header>
  )
}
